/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useMemo, useCallback } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import { isEmpty } from "lodash"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { SortableItemList } from "components/common"
import { useTranslationsMap } from "common/utils/i18n"
import { Item } from "types/models/menu"
import { isItemWithoutPhotoMode } from "utils/menu_helper"
import { mapItemsV2ToItems } from "utils/menuV3Mapper"
import { useMenuV3ItemsInfinite, flattenInfiniteItems } from "queries"
import { FoodmaxapipbEntityType } from "generated"
import LoadMore from "components/common/LoadMore"
import commonStyles from "styles/common.modules.css"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"
import { EmptyItems } from "../../empty/EmptyItems"
import { MenuItemRowV3 } from "./MenuItemRowV3"
import { MenuItemRowLoading } from "./MenuItemRowLoading"
import { MenuPartialError } from "./MenuPartialError"
import { useShowItemDrawer } from "../hooks/useShowItemDrawer"
import { buildMenuActionItems, MenuActionKey } from "./menuActionItems"

const rowHeight = 128

interface IProps {
  routePrefix?: string
}

export const MenuOverviewItemsV3: FC<IProps> = ({ routePrefix }: IProps) => {
  const dispatch = useDispatch()

  const { itemID: itemParam } = useParams<{
    itemID: string
  }>()

  const { isEditAllowed } = useMenuEditorStatus()
  const {
    merchantID,
    overview: {
      itemID,
      category: selectedCategory,
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
    },
    isMartDepartmentStructure,
    drawer: { showItemSelection },
    merchantGroupID,
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  // Determine parentType and parentID based on selected context
  // For mart department structure: only fetch when subdepartment is selected
  // For non-mart structure: fetch when category is selected
  const { parentType, parentID } = useMemo(() => {
    if (isMartDepartmentStructure) {
      // Only fetch items when subdepartment is selected (not just department)
      if (selectedSubDepartment?.itemClassID) {
        return {
          parentType: FoodmaxapipbEntityType.SubDepartment,
          parentID: selectedSubDepartment.itemClassID,
        }
      }
    } else if (selectedCategory?.categoryID) {
      return {
        parentType: FoodmaxapipbEntityType.Category,
        parentID: selectedCategory.categoryID,
      }
    }
    return { parentType: undefined, parentID: undefined }
  }, [isMartDepartmentStructure, selectedCategory, selectedSubDepartment])

  // Fetch items using MenuV3Items API
  // Use currentData instead of data to ensure we only get data for current query args
  // RTK Query creates separate cache keys for different parentID values
  const {
    currentData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMenuV3ItemsInfinite(
    {
      parentType,
      parentID,
      merchantID,
      merchantGroupID,
    },
    {
      skip: !parentID || !merchantID,
    },
  )

  // Flatten all pages and map to Item[]
  // Note: FoodmaxapipbItemV2 is structurally compatible with Item except for
  // restockSetting.freq (string vs RestockSettingFreq enum), so we cast here
  // The API response already contains hierarchy IDs, just need to map field names
  const allItems = useMemo(() => {
    const items = flattenInfiniteItems(currentData) || []
    return mapItemsV2ToItems(items)
  }, [currentData])

  // Filter items based on itemID and photo coverage
  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      let flag = isEmpty(itemID) ? true : item.itemID === itemID

      // if the item is ready to show, then check the feature flag and the dropdown value
      if (flag && isItemWithoutPhotoMode(itemFilter)) {
        flag = !(item.imageURL || (item.imageURLs && item.imageURLs.length > 0))
      }
      return flag
    })
  }, [allItems, itemID, itemFilter])

  // Load more handler
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const handleMenuClick = useCallback(
    (key: MenuActionKey) => {
      switch (key) {
        case MenuActionKey.REARRANGE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showSortItems: true,
            }),
          )
          break
        case MenuActionKey.SELECT_MULTIPLE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showItemSelection: true,
            }),
          )
          break
        default:
          break
      }
    },
    [dispatch],
  )

  const showItemDrawer = useShowItemDrawer({
    routePrefix,
    extraEventLabels: { IS_PARTIAL_MENU: true },
  })

  const textMap = useTranslationsMap({
    rearrange: "menu.drawers.sort.items",
    selectMultiple: "menu.actions.selectMultiple",
    itemsTitle: "menu.headings.items",
    sortItemsSuccess: "menu.notifications.sortItems.successMessage",
  })

  // deeplink when there is itemParam on first page load
  useEffect(() => {
    if (itemParam && allItems.length > 0) {
      const selectedItem = allItems.find(item => item.itemID === itemParam)
      if (selectedItem) {
        showItemDrawer(selectedItem)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemParam, allItems.length])

  const canShowRearrange =
    isEditAllowed &&
    isEmpty(itemID) &&
    filteredItems.length > 0 &&
    ![selectedCategory, selectedDepartment, selectedSubDepartment].every(
      item => item === null,
    )

  const menuItems = buildMenuActionItems({
    canShowRearrange,
    rearrange: {
      key: MenuActionKey.REARRANGE,
      label: textMap.rearrange,
      onClick: () => handleMenuClick(MenuActionKey.REARRANGE),
    },
    selectMultiple: {
      key: MenuActionKey.SELECT_MULTIPLE,
      label: textMap.selectMultiple,
      onClick: () => handleMenuClick(MenuActionKey.SELECT_MULTIPLE),
    },
  })

  const renderRow = (item: Item) => {
    return <MenuItemRowV3 item={item} showSelection={showItemSelection} />
  }

  const renderLoadMore = () => {
    if (hasNextPage && !error) {
      return (
        <LoadMore
          onLoadMore={handleLoadMore}
          component={<MenuItemRowLoading />}
        />
      )
    }
    return null
  }

  // Show loading state when:
  // 1. Initial load (isLoading) - no data for current query args yet
  // 2. Fetching but no currentData - means we're fetching for new query args
  // 3. Error exists but we're fetching (user clicked retry) - show loading immediately
  const isLoadingItem =
    isLoading || (isFetching && !currentData) || (error && isFetching)

  if (isLoadingItem) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.itemsTitle}</Col>
          </Row>
        }
      >
        <MenuItemRowLoading />
      </Card>
    )
  }

  // Show empty card when no parent selected (after loading check)
  // If loading, we show loading above. If not loading and no parent, show empty card
  if (!parentID) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.itemsTitle}</Col>
          </Row>
        }
      />
    )
  }

  // Show error state - if there's an error and we're not fetching, show only the error
  if (error) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.itemsTitle}</Col>
          </Row>
        }
      >
        <MenuPartialError error={error} onRefetch={refetch} />
      </Card>
    )
  }

  // Show empty state
  if (filteredItems.length === 0) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.itemsTitle}</Col>
          </Row>
        }
      >
        <EmptyItems />
      </Card>
    )
  }

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
      title={
        <Row justify="space-between">
          <Col>{textMap.itemsTitle}</Col>
          {isEditAllowed && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{ items: menuItems }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredItems}
        renderRowContent={renderRow}
        renderLoadMore={renderLoadMore}
        itemSize={rowHeight}
        onRowClick={(_, item: Item) => showItemDrawer(item)}
        useDragHandle
      />
    </Card>
  )
}
